import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/admin';
import { SiteSettings } from '../../types';
import { Card, Button } from '../../components/ui/Button';
import { Input, Alert } from '../../components/ui/Modal';
import { gs } from '../../styles/globalStyles';
import toast from 'react-hot-toast';
import { Ico } from '../../components/ui/Icon';

// Expose settings to the app — store in localStorage so other pages can read them
export const getAppSettings = (): SiteSettings => {
  try {
    const s = localStorage.getItem('app_settings');
    return s ? JSON.parse(s) : {};
  } catch { return {}; }
};

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SiteSettings>({});
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminService.getSettings();
      const s = res.data.settings || {};
      setSettings(s);
      localStorage.setItem('app_settings', JSON.stringify(s));
    } catch {
      // Fallback: use locally cached settings
      const cached = getAppSettings();
      if (Object.keys(cached).length) setSettings(cached);
    } finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminService.updateSettings(settings);
      // Persist to localStorage so frontend can use them
      localStorage.setItem('app_settings', JSON.stringify(settings));
      toast.success('Settings saved and applied!');
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const set = (key: keyof SiteSettings, value: string) =>
    setSettings((prev: SiteSettings) => ({ ...prev, [key]: value }));

  const sections = [
    {
      title: 'Platform',
      icon: '🏦',
      fields: [
        { key: 'site_name', label: 'Site Name', type: 'text', placeholder: 'XT' },
        { key: 'support_email', label: 'Support Email', type: 'email', placeholder: 'support@example.com' },
      ],
    },
    {
      title: 'Account Defaults',
      icon: '💰',
      fields: [
        { key: 'demo_balance', label: 'Default Demo Balance (USD)', type: 'number', placeholder: '10000' },
      ],
    },
    {
      title: 'Trading Limits',
      icon: '📊',
      fields: [
        { key: 'min_trade_amount', label: 'Min Trade Amount (USD)', type: 'number', placeholder: '10' },
        { key: 'max_trade_amount', label: 'Max Trade Amount (USD)', type: 'number', placeholder: '100000' },
        { key: 'max_leverage', label: 'Max Leverage', type: 'number', placeholder: '100' },
      ],
    },
    {
      title: 'Deposits & Withdrawals',
      icon: '💸',
      fields: [
        { key: 'min_withdrawal', label: 'Min Withdrawal (USD)', type: 'number', placeholder: '20' },
        { key: 'max_withdrawal', label: 'Max Withdrawal (USD)', type: 'number', placeholder: '50000' },
      ],
    },
    {
      title: 'Arbitrage System',
      icon: '⚡',
      fields: [
        { key: 'arbitrage_enabled', label: 'Enable Arbitrage (1=Yes, 0=No)', type: 'number', placeholder: '0' },
        { key: 'arbitrage_daily_percent', label: 'Default Daily Arbitrage %', type: 'number', placeholder: '0.5' },
      ],
    },
    {
      title: 'Security',
      icon: '🔐',
      fields: [
        { key: 'kyc_required', label: 'Require KYC for Withdrawal (1=Yes, 0=No)', type: 'number', placeholder: '0' },
        { key: 'maintenance_mode', label: 'Maintenance Mode (1=Yes, 0=No)', type: 'number', placeholder: '0' },
      ],
    },
  ];

  if (loading) return (
    <div className="p-6 flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full" />
    </div>
  );

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-gray-50 flex items-center gap-2">
          <Ico name="settings" className="w-4 h-4 text-primary" /> Platform Settings
        </h2>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={load} icon={<Ico name="refresh" className="w-3.5 h-3.5" />}>Reload</Button>
          <Button onClick={save} loading={saving} icon={<Ico name="save" className="w-3.5 h-3.5" />}>Save All</Button>
        </div>
      </div>

      <Alert type="info" message="Settings are saved to the server and applied to the frontend immediately on save." className="mb-6" />

      {settings.maintenance_mode === '1' && (
        <Alert type="error" title="Maintenance Mode is ON" message="Users cannot access the platform while maintenance mode is enabled." className="mb-4" />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {sections.map(section => (
          <Card key={section.title}>
            <h3 className="font-display font-semibold text-gray-50 mb-4 flex items-center gap-2">
              <span>{section.icon}</span> {section.title}
            </h3>
            <div className="flex flex-col gap-4">
              {section.fields.map(field => (
                <Input
                  key={field.key}
                  label={field.label}
                  type={field.type}
                  placeholder={field.placeholder}
                  value={(settings as any)[field.key] || ''}
                  onChange={e => set(field.key as keyof SiteSettings, e.target.value)}
                />
              ))}
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6">
        <Button onClick={save} loading={saving} fullWidth size="lg" icon={<Ico name="save" className="w-3.5 h-3.5" />}>
          Save All Settings
        </Button>
      </div>
    </div>
  );
};

export default Settings;
